import { inject, Injectable, InjectionToken, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import {
  Account as GqlAccount,
  Maybe,
  resolve,
  RoleName,
} from '../api/graphql/gqty';

export type Account = Pick<
  GqlAccount,
  | '__typename'
  | 'id'
  | 'firstName'
  | 'email'
  | 'lastName'
  | 'role'
  | 'isEnabled'
>;

const selectIdentity = (x: Maybe<GqlAccount> | undefined) => {
  return x
    ? ({
        __typename: x.__typename,
        id: x.id,
        firstName: x.firstName,
        email: x.email,
        lastName: x.lastName,
        role: x.role,
        isEnabled: x.isEnabled,
      } as const satisfies Account)
    : undefined;
};

export const USER = new InjectionToken<Account>('USER');

export const provideUser = () => ({
  provide: USER,
  useFactory: () => {
    const user = inject(AuthService).user();
    if (!user) {
      throw new Error('User not found in context');
    }
    return user;
  },
});

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly user = signal<Account | null>(null);

  getMe(): Promise<Account> {
    // Check if user is already cached
    const cachedUser = this.user();
    if (cachedUser) {
      return Promise.resolve(cachedUser);
    }

    // If not cached, fetch from API
    const promise = resolve(({ query }) => ({
      me: selectIdentity(query.me),
    })).then((result) => {
      if (!result.me) {
        throw new Error('Not authenticated');
      }
      // Cache the user data
      this.user.set(result.me);
      return result.me;
    });

    return Promise.resolve(promise);
  }

  refreshMe(): Promise<Account> {
    const promise = resolve(({ query }) => ({
      me: selectIdentity(query.me),
    })).then((result) => {
      if (!result.me) {
        throw new Error('Not authenticated');
      }
      // Update the cache with fresh data
      this.user.set(result.me);
      return result.me;
    });

    return Promise.resolve(promise);
  }

  isAdmin(): boolean {
    const user = this.user();
    return user?.role === RoleName.ADMIN;
  }

  logout(): void {
    // Clear the user cache
    this.user.set(null);
    window.location.href = environment.logoutFlowUrl;
  }
}
